// src/pages/attorney/AttorneyProfilePage.tsx
// BP-10-04: Attorney profile management page

import React, { useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  User,
  Building2,
  Award,
  FileCheck,
  Upload,
  Save,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  X,
  Globe,
  Phone,
  Mail,
} from 'lucide-react';
import { useAttorneyProfile } from '../hooks/useAttorney';
import { US_STATES } from '../types/attorney';

const profileSchema = z.object({
  firm_name: z.string().min(1, 'Firm name is required'),
  firm_website: z.string().url().optional().or(z.literal('')),
  bar_number: z.string().min(1, 'Bar number is required'),
  bar_state: z.string().min(1, 'Bar state is required'),
  additional_bar_states: z.array(z.string()).optional(),
  years_practicing: z.number().min(0).max(70),
  practice_areas: z.array(z.string()).min(1, 'At least one practice area required'),
  bio: z.string().max(2000).optional(),
  education: z.string().max(1000).optional(),
  notable_cases: z.string().max(2000).optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const PRACTICE_AREAS = [
  'Medical Malpractice',
  'Birth Injury',
  'Personal Injury',
  'Product Liability',
  'Wrongful Death',
  'Nursing Home Abuse',
  'Cerebral Palsy',
  'Brain Injury',
];

export const AttorneyProfilePage: React.FC = () => {
  const { profile, isLoading, updateProfile, isUpdating, uploadPhoto, submitVerification } = useAttorneyProfile();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firm_name: profile?.firm_name || '',
      firm_website: profile?.firm_website || '',
      bar_number: profile?.bar_number || '',
      bar_state: profile?.bar_state || '',
      additional_bar_states: profile?.additional_bar_states || [],
      years_practicing: profile?.years_practicing || 0,
      practice_areas: profile?.practice_areas || [],
      bio: profile?.bio || '',
      education: profile?.education || '',
      notable_cases: profile?.notable_cases || '',
    },
  });

  const selectedPracticeAreas = watch('practice_areas') || [];
  const additionalStates = watch('additional_bar_states') || [];

  const onSubmit = (data: ProfileFormData) => {
    updateProfile(data);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadPhoto(file);
    }
  };

  const togglePracticeArea = (area: string) => {
    const current = selectedPracticeAreas;
    if (current.includes(area)) {
      setValue('practice_areas', current.filter(a => a !== area), { shouldDirty: true });
    } else {
      setValue('practice_areas', [...current, area], { shouldDirty: true });
    }
  };

  const addBarState = (state: string) => {
    if (state && !additionalStates.includes(state)) {
      setValue('additional_bar_states', [...additionalStates, state], { shouldDirty: true });
    }
  };

  const removeBarState = (state: string) => {
    setValue('additional_bar_states', additionalStates.filter(s => s !== state), { shouldDirty: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">Attorney Profile</h1>
          <p className="text-warm-gray mt-1">Manage your professional information</p>
        </div>
        {profile?.is_verified ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
            <CheckCircle className="w-4 h-4" />
            Verified
          </div>
        ) : (
          <button
            onClick={() => setShowVerificationModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-lg hover:bg-terracotta-dark transition-colors"
          >
            <FileCheck className="w-4 h-4" />
            Get Verified
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Photo & Basic Info */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <div className="flex items-start gap-6">
            {/* Photo */}
            <div className="flex-shrink-0">
              <div className="relative">
                {profile?.photo_url ? (
                  <img
                    src={profile.photo_url}
                    alt="Profile"
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-cream flex items-center justify-center">
                    <User className="w-10 h-10 text-warm-gray" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-2 -right-2 p-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                >
                  <Upload className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Firm Info */}
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-1">
                  Firm Name *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                  <input
                    {...register('firm_name')}
                    className="w-full pl-10 pr-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
                    placeholder="Law Firm Name"
                  />
                </div>
                {errors.firm_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.firm_name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-1">
                  Firm Website
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                  <input
                    {...register('firm_website')}
                    type="url"
                    className="w-full pl-10 pr-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-1">
                  Years Practicing
                </label>
                <input
                  {...register('years_practicing', { valueAsNumber: true })}
                  type="number"
                  min="0"
                  max="70"
                  className="w-full px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bar Admission */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <h3 className="font-medium text-navy mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-sage" />
            Bar Admission
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Primary Bar Number *
              </label>
              <input
                {...register('bar_number')}
                className="w-full px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
                placeholder="123456"
              />
              {errors.bar_number && (
                <p className="text-red-500 text-xs mt-1">{errors.bar_number.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Primary Bar State *
              </label>
              <select
                {...register('bar_state')}
                className="w-full px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
              >
                <option value="">Select state...</option>
                {US_STATES.map(state => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.bar_state && (
                <p className="text-red-500 text-xs mt-1">{errors.bar_state.message}</p>
              )}
            </div>
          </div>

          {/* Additional Bar States */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Additional Bar Admissions
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {additionalStates.map(state => (
                <span
                  key={state}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-cream rounded-full text-sm"
                >
                  {US_STATES.find(s => s.value === state)?.label || state}
                  <button
                    type="button"
                    onClick={() => removeBarState(state)}
                    className="text-warm-gray hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <select
              onChange={(e) => {
                addBarState(e.target.value);
                e.target.value = '';
              }}
              className="px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage"
            >
              <option value="">Add state...</option>
              {US_STATES.filter(s => s.value !== watch('bar_state') && !additionalStates.includes(s.value)).map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Practice Areas */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <h3 className="font-medium text-navy mb-4">Practice Areas *</h3>
          <div className="flex flex-wrap gap-2">
            {PRACTICE_AREAS.map(area => (
              <button
                key={area}
                type="button"
                onClick={() => togglePracticeArea(area)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedPracticeAreas.includes(area)
                    ? 'bg-sage text-white'
                    : 'bg-cream text-navy hover:bg-cream-dark'
                }`}
              >
                {area}
              </button>
            ))}
          </div>
          {errors.practice_areas && (
            <p className="text-red-500 text-xs mt-2">{errors.practice_areas.message}</p>
          )}
        </div>

        {/* Bio & Background */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6 space-y-4">
          <h3 className="font-medium text-navy mb-4">Professional Background</h3>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">Bio</label>
            <textarea
              {...register('bio')}
              rows={4}
              className="w-full px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage resize-none"
              placeholder="Tell clients about your background and approach..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">Education</label>
            <textarea
              {...register('education')}
              rows={3}
              className="w-full px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage resize-none"
              placeholder="Law school, degrees, honors..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">Notable Cases</label>
            <textarea
              {...register('notable_cases')}
              rows={3}
              className="w-full px-4 py-2 border border-warm-gray/20 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage resize-none"
              placeholder="Significant verdicts or settlements..."
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!isDirty || isUpdating}
            className="flex items-center gap-2 px-6 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Changes
          </button>
        </div>
      </form>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-navy mb-4">Get Verified</h3>
            <p className="text-warm-gray text-sm mb-4">
              Upload your bar card or certificate to verify your attorney status.
              Verification typically takes 1-2 business days.
            </p>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  submitVerification(file);
                  setShowVerificationModal(false);
                }
              }}
              className="w-full mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowVerificationModal(false)}
                className="px-4 py-2 text-warm-gray hover:text-navy"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttorneyProfilePage;
